import React, { useMemo } from 'react';

/**
 * The brand's "voice data" motif: a mesh of thin waveform lines fading from violet to orange, the same art
 * the RoofVox site hero uses. Purely decorative, static (no animation, no cursor tracking) and drawn from the
 * theme tokens, so a tenant's accent/primary recolour it. Sized by its parent; anchor it to a panel's bottom.
 */
const W = 1200;
const H = 360;
const LINES = 34;
const STEPS = 96;

/** One wave line: a sum of two sines whose amplitude swells toward the middle of the canvas. */
function wavePath(index: number): string {
  const t = index / (LINES - 1); // 0..1 across the mesh
  const baseline = H * (0.92 - t * 0.34);
  const phase = index * 0.21;
  const points: string[] = [];
  for (let i = 0; i <= STEPS; i += 1) {
    const x = (i / STEPS) * W;
    const u = i / STEPS;
    const envelope = Math.sin(Math.PI * u) ** 1.4; // 0 at the edges, 1 mid-canvas
    const y =
      baseline -
      envelope * (46 + t * 70) * Math.sin(u * 9.4 + phase) -
      envelope * (18 + t * 26) * Math.sin(u * 22 - phase * 1.7);
    points.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return points.join(' ');
}

const WaveMesh: React.FC<{ className?: string }> = ({ className }) => {
  const paths = useMemo(() => Array.from({ length: LINES }, (_, i) => wavePath(i)), []);

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={className}
    >
      <defs>
        <linearGradient id="wave-mesh-stroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" style={{ stopColor: 'rgb(var(--ui-accent))' }} />
          <stop offset="55%" style={{ stopColor: 'rgb(var(--ui-accent))' }} />
          <stop offset="100%" style={{ stopColor: 'rgb(var(--ui-primary))' }} />
        </linearGradient>
      </defs>
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke="url(#wave-mesh-stroke)"
          strokeWidth={0.9}
          strokeLinecap="round"
          // Front lines read stronger than back lines, which is what gives the mesh its depth.
          opacity={0.14 + (i / (LINES - 1)) * 0.5}
        />
      ))}
    </svg>
  );
};

export default WaveMesh;
